import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useTextDomainContext, useUser } from "@/app/providers";
import {
  useCountries,
  usePageTitle,
  useSendVerificationEmail,
  useTimezones,
  useUpdateProfile,
} from "@/hooks";
import {
  ApiKeyModal,
  ChangePasswordModal,
  PictureUploadBox,
} from "@/components";
import { Box } from "@mui/system";
import { enqueueSnackbar } from "notistack";
import { languageList } from "@/utils";

const I18N_CONFIG_KEY = import.meta.env.VITE_I18N_CONFIG_KEY || "i18nConfig";

export const PersonaInformation = () => {
  //region Data
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  // Set page title
  usePageTitle(gettext("Profile Settings"));

  // Memoize allLanguages to prevent infinite re-renders
  const allLanguages = useMemo(() => languageList(gettext), [gettext]);

  const swaggerUrl = `${import.meta.env.VITE_API_URL}/v2/swagger`;

  const { countries, loading: countriesLoading } = useCountries();

  const { timezones, loading: timezoneLoading } = useTimezones();

  const { user } = useUser();

  const { updateProfile, loading: savingProfile } = useUpdateProfile(
    (res: any) => {
      if (res?.success === false) {
        const errorMsg = Array.isArray(res?.message)
          ? res.message[0]
          : res?.message || gettext("Failed to update profile.");
        enqueueSnackbar(errorMsg, { variant: "error" });
        return;
      }
      enqueueSnackbar(gettext("Your profile has been updated"), {
        variant: "success",
      });
    },
    (err: any) => {
      const errorMsg =
        err?.message ||
        gettext(
          "An unexpected error occurred. It has been logged, and we are working on a resolution.",
        );
      enqueueSnackbar(errorMsg, {
        variant: "error",
      });
    },
  );
  //endregion Data

  //region React Hooks (useState)
  const [inputName, setInputName] = useState("");
  const [inputEmail, setInputEmail] = useState("");
  const [inputLang, setInputLang] = useState(null);
  const [inputCountry, setInputCountry] = useState(null);
  const [inputTimezone, setInputTimezone] = useState(null);

  const [emailError, setEmailError] = useState(false);
  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [openApiKeyModal, setOpenApiKeyModal] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const isEmailVerified = Boolean(user?.isVerified);
  const [verificationSent, setVerificationSent] = useState(false);

  const { sendEmail: sendVerificationEmail, loading: sendingVerification } =
    useSendVerificationEmail(
      () => {
        setVerificationSent(true);
        enqueueSnackbar(
          gettext("A verification email has been sent to your email address."),
          { variant: "success" },
        );
      },
      (err: any) => {
        const msg =
          typeof err === "string"
            ? err
            : err?.message || gettext("Failed to send verification email.");
        enqueueSnackbar(msg, { variant: "error" });
      },
    );

  const handleResendVerification = () => {
    if (user?.email) {
      sendVerificationEmail(
        user.email,
        window.location.origin,
        user.lang || "en",
      );
    }
  };

  // Use refs to track if we've initialized to prevent circular updates
  const initializedRef = useRef({
    name: false,
    email: false,
    lang: false,
    country: false,
    timezone: false,
  });
  //endregion React Hooks (useState)

  //region React Hooks (useEffect)
  useEffect(() => {
    if (user?.roles) {
      const isSuper = user?.roles.includes("super-admin");
      setTimeout(() => {
        setIsSuperAdmin(isSuper);
      }, 0);
    }
  }, [user?.roles]);

  // Initialize name only once when user data is first available
  useEffect(() => {
    if (user?.name && !initializedRef.current.name) {
      setTimeout(() => {
        setInputName(user.name);
        initializedRef.current.name = true;
      }, 0);
    }
  }, [user?.name]);

  useEffect(() => {
    if (user?.email && !initializedRef.current.email) {
      setTimeout(() => {
        setInputEmail(user.email);
        initializedRef.current.email = true;
      }, 0);
    }
  }, [user?.email]);

  // Initialize language when both user lang and allLanguages are available
  useEffect(() => {
    if (user?.lang && allLanguages?.length && !initializedRef.current.lang) {
      const foundLang = allLanguages.find(
        (language) => language.lang === user.lang,
      );
      if (foundLang) {
        setTimeout(() => {
          setInputLang(foundLang);
          initializedRef.current.lang = true;
        }, 0);
      }
    }
  }, [user?.lang, allLanguages]);

  // Initialize country when both user countryCode and countries are available
  useEffect(() => {
    if (
      user?.countryCode &&
      countries?.length &&
      !initializedRef.current.country
    ) {
      const foundCountry = countries.find(
        (country) => country.code === user.countryCode,
      );
      if (foundCountry) {
        setTimeout(() => {
          setInputCountry(foundCountry);
          initializedRef.current.country = true;
        }, 0);
      }
    }
  }, [user?.countryCode, countries]);

  // Initialize timezone when both user timezone and timezones are available
  useEffect(() => {
    if (
      user?.timezone &&
      timezones?.length &&
      !initializedRef.current.timezone
    ) {
      const foundTimezone = timezones.find(
        (timezone: { zoneName?: string }) =>
          timezone.zoneName === user.timezone,
      );
      if (foundTimezone) {
        setTimeout(() => {
          setInputTimezone(foundTimezone);
          initializedRef.current.timezone = true;
        }, 0);
      }
    }
  }, [user?.timezone, timezones]);

  // Reset initialization flags when user changes (e.g., login/logout)
  useEffect(() => {
    if (!user?.id) {
      initializedRef.current = {
        name: false,
        email: false,
        lang: false,
        country: false,
        timezone: false,
      };
    }
  }, [user?.id]);
  //endregion React Hooks (useEffect)

  //region Handlers
  const handleChangePasswordClose = () => {
    setOpenChangePasswordModal(false);
  };

  const handleApiKeyClose = () => {
    setOpenApiKeyModal(false);
  };

  const handleNameChange = (value: string) => {
    setInputName(value);
  };

  const handleChangePasswordClick = () => {
    setOpenChangePasswordModal(true);
  };

  const handleApiKeyClick = () => {
    setOpenApiKeyModal(true);
  };

  const handlePictureChange = (file: File) => {
    setFileToUpload(file);
  };

  const handleSaveProfile = () => {
    const selectedCountryCode =
      (typeof inputCountry === "object"
        ? (inputCountry as any)?.code
        : inputCountry) || user?.countryCode;
    const selectedLang =
      (typeof inputLang === "object" ? (inputLang as any)?.lang : inputLang) ||
      user?.lang ||
      "en";
    const selectedTimezone =
      (typeof inputTimezone === "object"
        ? (inputTimezone as any)?.zoneName
        : inputTimezone) ||
      user?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "Europe/Copenhagen";

    const payload: any = {
      name: inputName?.trim() || user?.name || "",
      lang: selectedLang,
      countryCode: selectedCountryCode,
      timezone: selectedTimezone,
    };

    if (fileToUpload) {
      payload.file = fileToUpload;
    }

    updateProfile(payload);

    localStorage.setItem(
      I18N_CONFIG_KEY,
      JSON.stringify({
        selectedLang,
      }),
    );
  };
  //endregion Handlers

  return (
    <form
      className="card card-custom card-stretch"
      encType="multipart/form-data"
    >
      <Grid container spacing={2} sx={{ alignItems: "center" }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h4">{gettext("My Profile")}</Typography>
          <Typography variant="body1">
            {gettext("Update your Image and other data here.")}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {/*<Button*/}
            {/*  variant="outlined"*/}
            {/*  type="reset"*/}
            {/*  color="grey"*/}
            {/*  sx={{*/}
            {/*    m: 1,*/}
            {/*  }}*/}
            {/*>*/}
            {/*  {gettext("Cancel")}*/}
            {/*</Button>*/}
            {isSuperAdmin && (
              <>
                <Button
                  variant="contained"
                  href={swaggerUrl}
                  target="_blank"
                  // onClick={handleApiKeyClick}
                  sx={{
                    m: 1,
                    color: "secondary",
                  }}
                >
                  {gettext("API Documentation")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApiKeyClick}
                  sx={{
                    m: 1,
                    color: "secondary",
                  }}
                >
                  {gettext("API Key")}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              onClick={handleChangePasswordClick}
              sx={{
                m: 1,
                color: "secondary",
              }}
            >
              {gettext("Change Password")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveProfile}
              loading={savingProfile}
              disabled={countriesLoading || timezoneLoading}
              sx={{
                m: 1,
                color: "primary",
              }}
            >
              {gettext("Save Profile")}
            </Button>
          </Box>
        </Grid>

        {!isEmailVerified && (
          <Grid size={{ xs: 12 }}>
            <Alert
              severity="warning"
              sx={{
                width: "100%",
                alignItems: "center",
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={sendingVerification || verificationSent}
                  onClick={handleResendVerification}
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    textDecoration: "underline",
                  }}
                >
                  {sendingVerification
                    ? gettext("Sending...")
                    : verificationSent
                      ? gettext("Email sent")
                      : gettext("Resend verification email")}
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {verificationSent
                  ? gettext(
                      "Verification email has been sent to your email address. Please check your inbox.",
                    )
                  : gettext(
                      "Your email address is not verified. Please verify your email address.",
                    )}
              </Typography>
            </Alert>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "grey.200",
            }}
          ></Box>
        </Grid>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box sx={{ maxWidth: "md" }}>
          {/* region Personal Information */}
          <Grid container>
            <Grid
              container
              size={{ xs: 12 }}
              sx={{
                py: 3,
              }}
            >
              <Typography variant="h5">
                {gettext("Personal Information")}
              </Typography>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth margin="normal">
                  <FormLabel htmlFor="name">Name</FormLabel>
                  <TextField
                    type="text"
                    id="name"
                    placeholder={gettext("Full Name")}
                    size="small"
                    required
                    fullWidth
                    value={inputName || ""}
                    onChange={(e) => handleNameChange(e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth margin="normal">
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <TextField
                    type="email"
                    id="email"
                    placeholder={gettext("Enter email")}
                    size="small"
                    required
                    fullWidth
                    autoComplete="off"
                    value={inputEmail || ""}
                    onChange={(e) => {
                      setInputEmail(e.target.value);
                      setEmailError(false);
                    }}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    error={emailError}
                    helperText={
                      emailError &&
                      gettext("The mail already exists in the system!")
                    }
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth margin="normal">
                  <FormLabel htmlFor="country">Country</FormLabel>
                  <Autocomplete
                    disablePortal
                    options={countries}
                    getOptionLabel={(option: any) =>
                      option?.name || String(option) || ""
                    }
                    getOptionKey={(option: any) =>
                      option?.code || String(option) || ""
                    }
                    value={inputCountry || null}
                    onChange={(e, newValue) => {
                      setInputCountry(newValue);
                    }}
                    size="small"
                    fullWidth
                    isOptionEqualToValue={(option: any, value: any) => {
                      if (!value) return false;
                      const valCode =
                        typeof value === "string" ? value : value?.code;
                      return (
                        option?.code?.toLowerCase() === valCode?.toLowerCase()
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} required id="country" />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth margin="normal">
                  <FormLabel htmlFor="language">Language</FormLabel>
                  <Autocomplete
                    disablePortal
                    options={allLanguages}
                    getOptionLabel={(option: any) =>
                      option?.name || String(option) || ""
                    }
                    getOptionKey={(option: any) =>
                      option?.lang || String(option) || ""
                    }
                    value={inputLang || null}
                    onChange={(e, newValue) => setInputLang(newValue)}
                    size="small"
                    fullWidth
                    isOptionEqualToValue={(option: any, value: any) => {
                      if (!value) return false;
                      const valLang =
                        typeof value === "string" ? value : value?.lang;
                      return (
                        option?.lang?.toLowerCase() === valLang?.toLowerCase()
                      );
                    }}
                    renderInput={(params) => (
                      <TextField {...params} required id="language" />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          {/* endregion Personal Information */}

          {/* region Your Picture */}
          <Grid
            container
            sx={{
              py: 3,
            }}
          >
            <Typography variant="h5">{gettext("Your Picture")}</Typography>
          </Grid>
          <PictureUploadBox onChange={handlePictureChange} />
          {/* endregion Your Picture */}

          {/* region API Key */}
          {isSuperAdmin && (
            <>
              <Grid
                container
                sx={{
                  py: 3,
                }}
              >
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h5">{gettext("API Key")}</Typography>
                  <Typography variant="body1">
                    {gettext("Create or get an API key to access your data.")}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    onClick={handleApiKeyClick}
                    sx={{ color: "primary" }}
                  >
                    {gettext("API Key")}
                  </Button>
                </Grid>
              </Grid>
            </>
          )}
          {/* endregion Change */}
        </Box>
      </Grid>

      <ChangePasswordModal
        open={openChangePasswordModal}
        handleClose={handleChangePasswordClose}
      />

      <ApiKeyModal open={openApiKeyModal} handleClose={handleApiKeyClose} />
    </form>
  );
};
